import { useState, useEffect, Fragment } from 'react';
import { Flex, Select, Button, Text, theme } from '../2022-component-library';
import { useMediaQuery } from '../helpers/useMediaQuery';
import { keyframes } from '@emotion/react';
import { useCustomDeviceDimensions } from '../helpers/useCustomDeviceDimensions';
import { useAppointment } from '../context/appointmentContext';
import { customDeviceList } from '../helpers/customDeviceList';

const growAnimation = keyframes`
from {
height: 0px
}
to {
height: 100vh;
}`;

const ConfirmSelectionScreen = ({ make, model, setMakeValue, setModelValue }) => {
  const [patientSelection, setPatientSelection] = useState(false);
  const [showSubmissionConfirmation, setShowSubmissionConfirmation] = useState(false);
  const { appointment } = useAppointment();
  const sendDeviceDimensions = useCustomDeviceDimensions();

  useEffect(() => {
    if (appointment?.currentUser?.role === 'patient') setPatientSelection(true);
  }, [appointment]);

  const resetValues = () => {
    setMakeValue('default');
    setModelValue('default');
  };

  const highlightText = (strings, ...values) => (
    <span style={{ fontSize: 'h2' }}>
      {strings.map((string, index) => (
        <Fragment key={`string-${string}`}>
          {string}
          <span style={{ fontWeight: '700' }}>{values[index]}</span>
        </Fragment>
      ))}
    </span>
  );

  return (
    <Flex
      column
      centered
      animation={`${growAnimation} 300ms ease-out forwards`}
      backgroundColor='white'
      width='100vw'
      height='100vh'
      position='fixed'
      top='0'
      left='0'
      zIndex='1'
      padding='0.5rem'
      textAlign='center'
    >
      <Text>{highlightText`You have selected ${make} ${model} as your device. Is this correct?`}</Text>
      <Flex marginTop='1rem' width='100%' centered>
        <Button marginRight='0.5rem' width='20vw' maxWidth='200px' backgroundColor={theme.colors.error} onClick={() => resetValues()}>
          No
        </Button>
        <Button
          width='20vw'
          maxWidth='200px'
          onClick={() => {
            sendDeviceDimensions(make, model);
            if (patientSelection) return setShowSubmissionConfirmation(true);
            return resetValues();
          }}
        >
          Yes
        </Button>
      </Flex>
      {showSubmissionConfirmation && (
        <Text marginTop='2rem' fontWeight='700' color={theme.colors.secondaryDark}>
          Device submitted to clinician. Please wait for the next part of the test to begin.
        </Text>
      )}
    </Flex>
  );
};

const CustomDeviceSelection = () => {
  const [makeValue, setMakeValue] = useState('default');
  const [modelValue, setModelValue] = useState('default');
  const [deviceSelected, setDeviceSelected] = useState(false);
  const [deviceModelOptionsList, setDeviceModelOptionsList] = useState([]);
  const deviceMakeOptionsList = customDeviceList.map((devices) => devices.make);
  const smallScreen = useMediaQuery('(max-width: 700px)');

  useEffect(() => {
    if (makeValue !== 'default') {
      const selectedMake = customDeviceList.find((devices) => devices.make === makeValue);
      const modelOptions = selectedMake.models.map((model) => model.name);
      setDeviceModelOptionsList(modelOptions);
    }
  }, [makeValue]);

  useEffect(() => {
    if (makeValue !== 'default' && modelValue !== 'default') {
      const modelList = customDeviceList.find((device) => device.make === makeValue).models;
      const model = modelList.find((model) => model.name === modelValue);
      setDeviceSelected(model);
    } else setDeviceSelected(null);
  }, [makeValue, modelValue]);

  const handleSelection = (value, propertyChanged) => {
    if (propertyChanged === 'make') {
      setMakeValue(value);
    } else setModelValue(value);
  };

  return (
    <Flex width='100%' justifyContent='center' column={smallScreen} marginBottom={smallScreen ? 0 : '2rem'}>
      {deviceSelected && <ConfirmSelectionScreen make={makeValue} model={modelValue} setMakeValue={setMakeValue} setModelValue={setModelValue} />}
      <Select
        width={smallScreen ? '100%' : '50%'}
        marginBottom={smallScreen ? '1rem' : 0}
        marginRight={smallScreen ? 0 : '1rem'}
        name='select-device-make'
        id='select-device-make'
        label='Make'
        value={makeValue}
        onChange={(e) => handleSelection(e.target.value, 'make')}
      >
        <option value='default' disabled hidden>
          Select one...
        </option>
        {deviceMakeOptionsList.map((makeOption) => {
          return (
            <option key={`make-${makeOption}`} value={makeOption}>
              {makeOption}
            </option>
          );
        })}
      </Select>
      <Select
        width={smallScreen ? '100%' : '50%'}
        marginBottom={smallScreen ? '1rem' : 0}
        name='select-device-model'
        id='select-device-model'
        label='Model'
        value={modelValue}
        onChange={(e) => handleSelection(e.target.value, 'model')}
        visibility={!smallScreen || makeValue !== 'default' ? 'visible' : 'hidden'}
      >
        <option value='default' disabled hidden>
          Select one...
        </option>
        {deviceModelOptionsList.map((makeOption) => {
          return (
            <option key={`make-${makeOption}`} value={makeOption}>
              {makeOption}
            </option>
          );
        })}
      </Select>
    </Flex>
  );
};

export default CustomDeviceSelection;
